/**
 * O que se concede a quem opera a plataforma.
 *
 * Espelho de `app.permissoes_da_plataforma()` (migração `0090`):
 * `tests/unit/permissoes-da-plataforma.test.ts` lê a migração e falha se as duas
 * listas divergirem. A tela usa esta lista para montar o formulário; quem DECIDE
 * é o banco, que recusa o que não pode.
 *
 * `*` não está na lista: é o acesso total, e só quem o tem o concede.
 */

export interface PermissaoDaPlataforma {
    id: string;
    rotulo: string;
    grupo: 'Base' | 'Clientes' | 'Cobrança' | 'Plataforma' | 'Suporte';
}

export const PERMISSOES_DA_PLATAFORMA: PermissaoDaPlataforma[] = [
    { id: 'platform', rotulo: 'Entrar no painel da plataforma', grupo: 'Base' },
    { id: 'clientes.ver', rotulo: 'Ver as clínicas e a ficha de cada uma', grupo: 'Clientes' },
    { id: 'clientes.editar', rotulo: 'Abrir clínica nova', grupo: 'Clientes' },
    { id: 'clientes.suspender', rotulo: 'Suspender e reativar clínica', grupo: 'Clientes' },
    { id: 'modulos.alternar', rotulo: 'Ligar e desligar módulo por clínica', grupo: 'Clientes' },
    { id: 'plano.alterar', rotulo: 'Trocar o plano de uma clínica e dar desconto', grupo: 'Cobrança' },
    { id: 'planos.editar', rotulo: 'Editar o catálogo de planos', grupo: 'Cobrança' },
    { id: 'faturamento.gerenciar', rotulo: 'Gerar faturas e emitir fatura avulsa', grupo: 'Cobrança' },
    { id: 'financeiro.operar', rotulo: 'Abrir o faturamento e registrar pagamento', grupo: 'Cobrança' },
    { id: 'estorno.executar', rotulo: 'Registrar estorno de fatura', grupo: 'Cobrança' },
    { id: 'creditos.adicionar', rotulo: 'Conceder crédito', grupo: 'Cobrança' },
    { id: 'creditos.alterar', rotulo: 'Editar crédito ainda não usado', grupo: 'Cobrança' },
    { id: 'creditos.excluir', rotulo: 'Excluir crédito', grupo: 'Cobrança' },
    { id: 'flags.editar', rotulo: 'Mudar padrão e exceção das flags', grupo: 'Plataforma' },
    { id: 'flags.alternar', rotulo: 'Rollout e kill switch das flags', grupo: 'Plataforma' },
    { id: 'integracoes.editar', rotulo: 'Chaves de IA e servidores de canal', grupo: 'Plataforma' },
    { id: 'usuarios.gerenciar', rotulo: 'Gerenciar a equipe da plataforma', grupo: 'Plataforma' },
    { id: 'ia.tetos', rotulo: 'Definir o teto de consumo de IA por clínica', grupo: 'Plataforma' },
    { id: 'impersonar', rotulo: 'Entrar numa clínica — somente leitura, auditada', grupo: 'Suporte' },
    { id: 'suporte.atender', rotulo: 'Registrar e atender chamado de suporte', grupo: 'Suporte' },
    { id: 'auditoria.ver', rotulo: 'Ler o log de auditoria', grupo: 'Suporte' },
];

/** A mesma leitura do banco: a permissão exata, o módulo antes do ponto, ou `*`. */
export function operadorTem(minhas: readonly string[], permissao: string): boolean {
    if (permissao === '*') return minhas.includes('*');
    return minhas.includes('*') || minhas.includes(permissao) || minhas.includes(permissao.split('.')[0] ?? permissao);
}

/**
 * O que o banco recusaria ao definir as permissões de outro operador — ou nulo.
 *
 * Existe para a demonstração recusar do mesmo jeito e para a tela desligar a
 * caixa que não pode mudar. Não é a trava: a trava é
 * `definir_permissoes_do_operador`.
 */
export function recusaAoDefinirPermissoes(
    minhas: readonly string[],
    antes: readonly string[],
    depois: readonly string[],
    ehEuMesmo: boolean,
): string | null {
    if (!operadorTem(minhas, 'usuarios.gerenciar')) return 'Só quem gerencia os usuários da plataforma muda permissões.';
    if (ehEuMesmo) return 'Ninguém muda as próprias permissões.';
    const conhecidas = new Set([...PERMISSOES_DA_PLATAFORMA.map((p) => p.id), '*']);
    const desconhecida = depois.find((p) => !conhecidas.has(p));
    if (desconhecida) return `Permissão desconhecida: ${desconhecida}.`;
    const mudadas = [...new Set([...antes, ...depois])].filter((p) => antes.includes(p) !== depois.includes(p));
    for (const p of mudadas) {
        if (p === '*' && !minhas.includes('*')) return 'Só quem tem acesso total concede ou retira o acesso total.';
        if (p !== '*' && !operadorTem(minhas, p)) return `Você não pode conceder nem retirar "${p}", porque não a tem.`;
    }
    return null;
}
